import * as React from 'react';

import { useRegulationsFormikRef } from './RegulationsFormikContext';

/**
 * Backs a single Regulations field with local component state instead of Formik's context.
 *
 * The input's visible value lives in local state (so keystrokes only re-render this one field),
 * while every change is also written straight into the Formik bag via the stable `formikRef` so
 * submit-time diffing (see RegulationsPanelForm) keeps working exactly as before. Because nothing
 * here subscribes to Formik's context, this hook - and the component using it - never re-renders
 * when *other* fields change.
 *
 * `initialValue` re-syncs the local state only when it changes for real (new server data / a
 * field remount on reset), never because of Formik's own internal churn.
 */
export const useRegulationsFieldValue = <T = any>(name: string, initialValue: T) => {
  const formikRef = useRegulationsFormikRef();
  const [value, setValue] = React.useState<T>(initialValue);

  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue, setValue]);

  const commit = React.useCallback(
    (nextValue: T) => {
      setValue(nextValue);
      formikRef.current?.setFieldValue(name, nextValue, false);
    },
    [formikRef, name],
  );

  return [value, commit] as const;
};
